import styled from 'styled-components';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { Tooltip } from '@mui/material';
import { Flex } from '../../Flex';
import { pluralizeString } from '../../../utils/common';
import { ToolInvocationEvent } from '../types';
import { ToolText, formatToolName } from './toolDisplay';

const Chip = styled(Flex)`
  align-items: center;
  gap: 8px;
  width: fit-content;
  max-width: 100%;
`;

const Label = styled(ToolText)`
  white-space: nowrap;
  flex-shrink: 0;
`;

const PreviewText = styled.span`
  color: ${({ theme }) => theme.palette.common.gray12};
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

type Invocation = ToolInvocationEvent['toolInvocation'];

const INTERNAL_SKILL_PATH_RE = /^\/skills(-extended)?\/[^/]+\/SKILL\.md$/;

export const isInternalSkillRead = (invocation: Invocation): boolean => {
  if (invocation.toolName !== 'read_file') return false;
  const filePath = invocation.args?.file_path;
  return typeof filePath === 'string' && INTERNAL_SKILL_PATH_RE.test(filePath);
};

const TOOL_LABEL_OVERRIDES: Record<string, string> = {
  discover_tool: 'Preparing tools',
  read_test_config_wizard: 'Reading the test form',
  write_test_config_wizard: 'Updating the test form',
  read_environment_form: 'Reading the environment form',
  write_environment_form: 'Updating the environment form',
};

export const getToolLabelOverride = (invocation: Invocation): string | null => {
  if (isInternalSkillRead(invocation)) return 'Exploring skills';
  return TOOL_LABEL_OVERRIDES[invocation.toolName ?? ''] ?? null;
};

export const stringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const getArgsPreview = (
  args?: Record<string, unknown>,
): string | null => {
  if (!args) return null;
  const goal = args.goal;
  if (typeof goal === 'string' && goal.trim()) {
    return goal.trim();
  }
  const entries = Object.entries(args).filter(
    ([, val]) => val !== undefined && val !== null && val !== '',
  );
  if (entries.length === 0) return null;
  const [key, val] = entries[0];
  const valStr = typeof val === 'string' ? val : stringify(val);
  return `${key}: ${valStr}`;
};

export const getRowCount = (invocation: Invocation): number | null => {
  const result = invocation.result?.result;
  if (Array.isArray(result)) return result.length;
  if (result && typeof result === 'object' && Array.isArray(result.rows)) {
    return result.rows.length;
  }
  return null;
};

type Props = {
  invocation: Invocation;
};

const ToolCallChip = ({ invocation }: Props) => {
  const isRunning = invocation.state === 'call';
  const isError = !!invocation.result?.isError;
  const labelOverride = getToolLabelOverride(invocation);

  const StatusIcon = isError
    ? ErrorRoundedIcon
    : isRunning
    ? SearchRoundedIcon
    : CheckCircleRoundedIcon;

  const label = labelOverride ?? formatToolName(invocation.toolName);
  const preview = labelOverride ? null : getArgsPreview(invocation.args);
  const rowCount = getRowCount(invocation);

  return (
    <Chip>
      <StatusIcon
        sx={{ fontSize: '18px', color: 'common.magenta', flexShrink: 0 }}
      />
      <Label $running={isRunning}>{label}</Label>
      {preview && (
        <Tooltip title={preview} placement="top-start">
          <PreviewText>{preview}</PreviewText>
        </Tooltip>
      )}
      {rowCount !== null && (
        <PreviewText>· {pluralizeString(rowCount, 'row', 'rows')}</PreviewText>
      )}
    </Chip>
  );
};

export default ToolCallChip;
